import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Col,
  Row,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Dropdown,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function AutoPayrollCalculation() {
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [clockEntries, setClockEntries] = useState([]);
  const [calculatedPayrolls, setCalculatedPayrolls] = useState([]);
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedUser, setSelectedUser] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayrollDetails, setSelectedPayrollDetails] = useState(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDocRef = doc(db, "Users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userRole = userDoc.data().role || "";
            setCurrentUserRole(userRole);

            if (userRole === "admin") {
              setRolePermissions({ canManagePayrolls: true });
            } else {
              const roleDocRef = doc(db, "Roles", userRole);
              const roleDoc = await getDoc(roleDocRef);

              if (roleDoc.exists()) {
                setRolePermissions(roleDoc.data().permissions);
              } else {
                setRolePermissions(null);
              }
            }
          }
        }
      });
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (rolePermissions && !rolePermissions.canManagePayrolls && currentUserRole !== "admin") {
      setAccessDenied(true);
    }
  }, [rolePermissions]);

  useEffect(() => {
    if (currentUserRole === "admin" || (rolePermissions && rolePermissions.canManagePayrolls)) {
      fetchAllData();
    }
  }, [currentUserRole, rolePermissions]);

  useEffect(() => {
    if (users.length > 0 && payrolls.length > 0 && clockEntries.length > 0) {
      calculatePayrolls();
    }
  }, [users, payrolls, clockEntries, selectedMonth, selectedUser]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch users
      const usersQuery = await getDocs(collection(db, "Users"));
      const usersData = usersQuery.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);

      // Fetch payrolls
      const payrollQuery = await getDocs(collection(db, "payrolls"));
      const payrollData = payrollQuery.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayrolls(payrollData);

      // Fetch clock entries
      const clockQuery = await getDocs(collection(db, "clock_entries"));
      const clockData = clockQuery.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClockEntries(clockData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePayrolls = () => {
    const startOfMonth = new Date(`${selectedMonth}-01T00:00:00`);
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0, 23, 59, 59);
    
    const calculated = users.map(user => {
      // Get user's payroll settings
      const userPayroll = payrolls.find(p => p.email === user.email);
      if (!userPayroll) return null;

      // Get user's clock entries for the selected month
      const userClockEntries = clockEntries.filter(entry => {
        const clockIn = new Date(entry.clockIn);
        return entry.email === user.email && 
               clockIn >= startOfMonth && 
               clockIn <= endOfMonth &&
               entry.clockOut; // Only completed entries
      });

      // Calculate total hours worked
      let totalHours = 0;
      let totalBreakMinutes = 0;
      const dailyEntries = [];

      userClockEntries.forEach(entry => {
        const clockIn = new Date(entry.clockIn);
        const clockOut = new Date(entry.clockOut);
        const duration = (clockOut - clockIn) / (1000 * 60 * 60); // Convert to hours
        
        // Calculate break time
        const breaks = entry.breaks || [];
        const breakMinutes = breaks.reduce((sum, breakItem) => sum + (breakItem.duration || 0), 0);
        totalBreakMinutes += breakMinutes;
        
        const netHours = duration - (breakMinutes / 60);
        totalHours += netHours;

        dailyEntries.push({
          date: clockIn.toISOString().split('T')[0],
          clockIn: clockIn.toLocaleTimeString(),
          clockOut: clockOut.toLocaleTimeString(),
          totalHours: duration.toFixed(2),
          breakMinutes: breakMinutes,
          netHours: netHours.toFixed(2)
        });
      });

      // Calculate payroll
      const hourlyRate = userPayroll.biweeklySalary / userPayroll.biweeklyHours;
      const grossPay = totalHours * hourlyRate;
      const breakHours = totalBreakMinutes / 60;

      return {
        userId: user.id,
        email: user.email,
        fullName: user.fullName || user.email,
        role: user.role || 'Employee',
        totalHours: totalHours.toFixed(2),
        breakHours: breakHours.toFixed(2),
        hourlyRate: hourlyRate.toFixed(2),
        grossPay: grossPay.toFixed(2),
        dailyEntries,
        payrollSettings: userPayroll,
        month: selectedMonth
      };
    }).filter(Boolean);

    setCalculatedPayrolls(calculated);
  };

  const filteredPayrolls = useMemo(() => {
    let filtered = calculatedPayrolls;

    if (selectedUser) {
      filtered = filtered.filter(p => p.email === selectedUser);
    }

    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [calculatedPayrolls, selectedUser, searchQuery]);

  const handleViewDetails = (payroll) => {
    setSelectedPayrollDetails(payroll);
    setShowDetailsModal(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Employee Name', 'Email', 'Role', 'Total Hours', 'Break Hours', 'Hourly Rate', 'Gross Pay', 'Month'],
      ...filteredPayrolls.map(p => [
        p.fullName,
        p.email,
        p.role,
        p.totalHours,
        p.breakHours,
        p.hourlyRate,
        p.grossPay,
        p.month
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (accessDenied) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="bg-gradient-danger text-white rounded-4 p-4 shadow-lg">
            <i className="ri-error-warning-line fs-1 mb-3"></i>
            <h6 className="fw-bold">Access Denied</h6>
            <p className="mb-0">You don't have permission to access this page.</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="text-center">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 text-muted">Loading payroll calculations...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Header Section */}
        <div className="d-flex align-items-center mb-4">
          <div className="me-3">
            <i className="ri-calculator-line display-6 text-primary"></i>
          </div>
          <div>
            <h1 className="display-6 fw-bold text-primary mb-1">Auto Payroll Calculation</h1>
            <p className="lead text-muted mb-0">Automatically calculated payrolls based on clock entries and rates</p>
          </div>
        </div>

        {/* Filters Section */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <Card.Header className="bg-gradient-primary text-white p-4 rounded-top-4">
            <div className="d-flex align-items-center">
              <i className="ri-filter-line me-3 fs-4"></i>
              <h4 className="fw-bold mb-0">Filters & Search</h4>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <i className="ri-calendar-line me-2"></i>
                    Select Month
                  </Form.Label>
                  <Form.Control
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <i className="ri-user-search-line me-2"></i>
                    Filter by Employee
                  </Form.Label>
                  <Form.Select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  >
                    <option value="">All Employees</option>
                    {users.map(user => (
                      <option key={user.id} value={user.email}>
                        {user.fullName || user.email}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <i className="ri-search-line me-2"></i>
                    Search Employee
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Summary Cards */}
        <Row className="g-4 mb-5">
          <Col md={3}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
              <Card.Header 
                className="bg-gradient-primary text-white p-4"
                style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
              >
                <div className="d-flex align-items-center">
                  <i className="ri-user-line fs-4 me-3"></i>
                  <h6 className="fw-bold mb-0">Total Employees</h6>
                </div>
              </Card.Header>
              <Card.Body className="p-4 text-center">
                <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                  <i className="ri-user-line fs-1 text-primary"></i>
                </div>
                <h3 className="fw-bold text-primary">{filteredPayrolls.length}</h3>
                <p className="text-muted small mb-0">Active employees</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
              <Card.Header 
                className="bg-gradient-success text-white p-4"
                style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}
              >
                <div className="d-flex align-items-center">
                  <i className="ri-time-line fs-4 me-3"></i>
                  <h6 className="fw-bold mb-0">Total Hours</h6>
                </div>
              </Card.Header>
              <Card.Body className="p-4 text-center">
                <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                  <i className="ri-time-line fs-1 text-success"></i>
                </div>
                <h3 className="fw-bold text-success">
                  {filteredPayrolls.reduce((sum, p) => sum + parseFloat(p.totalHours), 0).toFixed(1)}
                </h3>
                <p className="text-muted small mb-0">Hours worked</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
              <Card.Header 
                className="bg-gradient-warning text-white p-4"
                style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
              >
                <div className="d-flex align-items-center">
                  <i className="ri-money-dollar-circle-line fs-4 me-3"></i>
                  <h6 className="fw-bold mb-0">Total Payroll</h6>
                </div>
              </Card.Header>
              <Card.Body className="p-4 text-center">
                <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                  <i className="ri-money-dollar-circle-line fs-1 text-warning"></i>
                </div>
                <h3 className="fw-bold text-warning">
                  ${filteredPayrolls.reduce((sum, p) => sum + parseFloat(p.grossPay), 0).toFixed(2)}
                </h3>
                <p className="text-muted small mb-0">Gross pay</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
              <Card.Header 
                className="bg-gradient-info text-white p-4"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                <div className="d-flex align-items-center">
                  <i className="ri-calendar-line fs-4 me-3"></i>
                  <h6 className="fw-bold mb-0">Selected Month</h6>
                </div>
              </Card.Header>
              <Card.Body className="p-4 text-center">
                <div className="bg-info bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                  <i className="ri-calendar-line fs-1 text-info"></i>
                </div>
                <h3 className="fw-bold text-info">
                  {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </h3>
                <p className="text-muted small mb-0">Payroll period</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Payroll Table */}
        <Card className="border-0 shadow-lg rounded-4">
          <Card.Header className="bg-gradient-success text-white p-4 rounded-top-4">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-file-list-3-line fs-4 me-3"></i>
                <h4 className="fw-bold mb-0">Calculated Payrolls</h4>
                <Badge bg="light" text="dark" className="ms-3 px-3 py-2">
                  {filteredPayrolls.length} employees
                </Badge>
              </div>
              <Button
                variant="light"
                onClick={exportToCSV}
                className="px-4 py-2 rounded-pill fw-semibold"
              >
                <i className="ri-download-line me-2"></i>
                Export CSV
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {filteredPayrolls.length === 0 ? (
              <div className="text-center py-5">
                <i className="ri-file-list-3-line display-1 text-muted mb-3"></i>
                <h5 className="text-muted">No payroll data found</h5>
                <p className="text-muted">Try adjusting your filters or check if employees have clock entries for the selected month.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-hover table-borderless mb-0">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="border-0 p-3">
                        <i className="ri-user-line me-2"></i>
                        Employee
                      </th>
                      <th className="border-0 p-3">
                        <i className="ri-time-line me-2"></i>
                        Total Hours
                      </th>
                      <th className="border-0 p-3">
                        <i className="ri-pause-circle-line me-2"></i>
                        Break Hours
                      </th>
                      <th className="border-0 p-3">
                        <i className="ri-money-dollar-circle-line me-2"></i>
                        Hourly Rate
                      </th>
                      <th className="border-0 p-3">
                        <i className="ri-calculator-line me-2"></i>
                        Gross Pay
                      </th>
                      <th className="border-0 p-3">
                        <i className="ri-settings-3-line me-2"></i>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayrolls.map((payroll, index) => (
                      <tr 
                        key={payroll.userId}
                        style={{ 
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.target.closest('tr').style.backgroundColor = '#f8f9fa';
                          e.target.closest('tr').style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.closest('tr').style.backgroundColor = 'transparent';
                          e.target.closest('tr').style.transform = 'translateY(0)';
                        }}
                      >
                        <td className="p-3">
                          <div>
                            <h6 className="fw-semibold mb-1">{payroll.fullName}</h6>
                            <small className="text-muted">{payroll.email}</small>
                            <br />
                            <Badge bg="secondary" className="mt-1">
                              {payroll.role}
                            </Badge>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge bg="success" className="rounded-pill px-3 py-2">
                            {payroll.totalHours}h
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge bg="warning" text="dark" className="rounded-pill px-3 py-2">
                            {payroll.breakHours}h
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="fw-semibold">${payroll.hourlyRate}</span>
                        </td>
                        <td className="p-3">
                          <span className="fw-bold text-success fs-5">${payroll.grossPay}</span>
                        </td>
                        <td className="p-3">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleViewDetails(payroll)}
                            className="rounded-pill px-3 py-2"
                          >
                            <i className="ri-eye-line me-1"></i>
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Details Modal */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          <Modal.Header className="bg-gradient-primary text-white border-0">
            <Modal.Title>
              <i className="ri-file-text-line me-2"></i>
              Payroll Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedPayrollDetails && (
              <>
                {/* Employee Info */}
                <Card className="border-0 bg-primary bg-opacity-10 rounded-3 p-4 mb-4">
                  <div className="text-center">
                    <i className="ri-user-line display-4 text-primary mb-3"></i>
                    <h4 className="fw-bold text-primary">{selectedPayrollDetails.fullName}</h4>
                    <p className="text-muted mb-0">{selectedPayrollDetails.email}</p>
                    <Badge bg="primary" className="mt-2">
                      {selectedPayrollDetails.role}
                    </Badge>
                  </div>
                </Card>

                {/* Payroll Summary */}
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Card className="border-0 bg-light rounded-3 p-3 h-100">
                      <div className="d-flex align-items-center">
                        <i className="ri-time-line fs-4 text-success me-3"></i>
                        <div>
                          <h6 className="fw-bold text-primary mb-1">Total Hours</h6>
                          <p className="mb-0 fw-semibold">{selectedPayrollDetails.totalHours} hours</p>
                        </div>
                      </div>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light rounded-3 p-3 h-100">
                      <div className="d-flex align-items-center">
                        <i className="ri-money-dollar-circle-line fs-4 text-warning me-3"></i>
                        <div>
                          <h6 className="fw-bold text-primary mb-1">Gross Pay</h6>
                          <p className="mb-0 fw-semibold">${selectedPayrollDetails.grossPay}</p>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* Daily Entries */}
                <Card className="border-0 bg-light rounded-3 p-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="ri-calendar-line me-2"></i>
                    Daily Clock Entries
                  </h6>
                  <div className="table-responsive">
                    <Table className="table-sm table-borderless">
                      <thead>
                        <tr className="border-bottom">
                          <th>Date</th>
                          <th>Clock In</th>
                          <th>Clock Out</th>
                          <th>Total Hours</th>
                          <th>Break Time</th>
                          <th>Net Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPayrollDetails.dailyEntries.map((entry, index) => (
                          <tr key={index}>
                            <td>{new Date(entry.date).toLocaleDateString()}</td>
                            <td>{entry.clockIn}</td>
                            <td>{entry.clockOut}</td>
                            <td>{entry.totalHours}h</td>
                            <td>{entry.breakMinutes}m</td>
                            <td className="fw-semibold">{entry.netHours}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 p-4">
            <Button
              variant="secondary"
              onClick={() => setShowDetailsModal(false)}
              className="px-4 py-2 rounded-pill fw-semibold"
            >
              <i className="ri-close-line me-2"></i>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Footer />
    </>
  );
}
